import React, { useEffect, useState } from 'react';
import Table from '../Table/Table';
import config from '../../config';

function SupplierChequePayments() {

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [error, setError] = useState(null);
    const [pendingChequeAmount, setPendingChequeAmount] = useState(0);
    const [clearedChequeAmount, setClearedChequeAmount] = useState(0);
    const [pendingChequeCount, setPendingChequeCount] = useState(0);
    const [statusFilter, setStatusFilter] = useState('all');
    const [groupedData, setGroupedData] = useState([]);

    const columns = ['Date of Issue', 'Supplier Name', 'Cheque Numbers', 'Cheque Dates', 'Total Amount', 'Status'];

    useEffect(() => {
        fetchChequeList();
        fetchPendingChequeCount();
    });

    useEffect(() => {
        filterData();
    }, [statusFilter, groupedData]);

    const filterData = () => {
        if (statusFilter === 'all') {
            setFilteredData(groupedData);
        } else {
            const filtered = groupedData.filter(row =>
                row[5].props.value === statusFilter
            );
            setFilteredData(filtered);
        }
    };

    const groupChequesByStockPayment = (cheques) => {
        const groupedCheques = {};

        cheques.forEach(cheque => {
            const stockPaymentId = cheque.stockPaymentId || 'unknown';

            if (!groupedCheques[stockPaymentId]) {
                groupedCheques[stockPaymentId] = {
                    stockPaymentId: stockPaymentId,
                    issuedDate: cheque.issuedDate,
                    supplier: cheque.supplier,
                    cheques: [],
                    chequeNumbers: [],
                    chequeDates: [],
                    totalAmount: 0,
                    allCleared: true,
                    anyPending: false,
                    chequeIds: []
                };
            }

            groupedCheques[stockPaymentId].cheques.push(cheque);
            groupedCheques[stockPaymentId].chequeNumbers.push(cheque.chequeNumber);

            const chequeDate = new Date(cheque.chequeDate);
            const formattedChequeDate = `${chequeDate.getFullYear()}-${String(chequeDate.getMonth() + 1).padStart(2, '0')}-${String(chequeDate.getDate()).padStart(2, '0')}`;
            groupedCheques[stockPaymentId].chequeDates.push(formattedChequeDate);

            groupedCheques[stockPaymentId].totalAmount += Number(cheque.chequeAmount || 0);

            if (cheque.chequeStatus === 'Pending') {
                groupedCheques[stockPaymentId].allCleared = false;
                groupedCheques[stockPaymentId].anyPending = true;
            }

            groupedCheques[stockPaymentId].chequeIds.push(cheque.chequeId);
        });

        return Object.values(groupedCheques);
    };

    const fetchChequeList = async () => {
        try {
            const response = await fetch(`${config.BASE_URL}/cheques`);
            if (!response.ok) throw new Error(`Failed to fetch cheques: ${response.status}`);
            const cheques = await response.json();

            const updatePromises = cheques.map(async (cheque) => {
                if (cheque.chequeStatus === "Pending") {
                    const chequeDate = new Date(cheque.chequeDate);
                    const today = new Date();

                    chequeDate.setHours(0, 0, 0, 0);
                    const twoDaysLater = new Date(chequeDate);
                    twoDaysLater.setDate(chequeDate.getDate() + 2);

                    today.setHours(0, 0, 0, 0);

                    if (today > twoDaysLater) {
                        try {
                            await fetch(`${config.BASE_URL}/cheque/${cheque.chequeId}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ chequeStatus: "Cleared" })
                            });
                        } catch (error) {
                            console.error("Update failed:", error);
                        }
                    }
                }
            });

            await Promise.all(updatePromises);

            const updatedResponse = await fetch(`${config.BASE_URL}/cheques`);
            if (!updatedResponse.ok) throw new Error('Failed to fetch updated cheques');
            const updatedCheques = await updatedResponse.json();

            const grouped = groupChequesByStockPayment(updatedCheques);

            const sortedGroups = [...grouped].sort((a, b) => {
                if (a.anyPending && !b.anyPending) return -1;
                if (!a.anyPending && b.anyPending) return 1;
                return new Date(a.issuedDate) - new Date(b.issuedDate);
            });

            const formattedData = sortedGroups.map((group) => {
                const issuedDate = new Date(group.issuedDate);
                const formattedIssuedDate = `${issuedDate.getFullYear()}-${String(issuedDate.getMonth() + 1).padStart(2, '0')}-${String(issuedDate.getDate()).padStart(2, '0')} ${String(issuedDate.getHours()).padStart(2, '0')}:${String(issuedDate.getMinutes()).padStart(2, '0')}`;

                const status = group.allCleared ? "Cleared" : "Pending";

                const chequeNumbersList = group.chequeNumbers.join(' | ');
                const chequeDatesList = group.chequeDates.join(' | ');

                return [
                    formattedIssuedDate || '-',
                    group.supplier?.supplierName || '-',
                    chequeNumbersList || '-',
                    chequeDatesList || '-',
                    `LKR ${Number(group.totalAmount).toLocaleString()}`,
                    <select
                        className="form-control"
                        value={status}
                        onChange={(e) => handleGroupStatusChange(group.chequeIds, e.target.value)}
                        disabled={status === "Cleared"}
                    >
                        <option value="Pending">Pending</option>
                        <option value="Cleared">Cleared</option>
                    </select>,
                ];
            });

            setGroupedData(formattedData);
            setData(formattedData);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleGroupStatusChange = async (chequeIds, newStatus) => {
        try {
            const updatePromises = chequeIds.map(id =>
                fetch(`${config.BASE_URL}/cheque/${id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ chequeStatus: newStatus }),
                })
            );

            await Promise.all(updatePromises);

            fetchChequeList();
            fetchPendingChequeCount();
        } catch (error) {
            setError(error.message);
        }
    };

    const fetchPendingChequeCount = async () => {
        try {
            const response = await fetch(`${config.BASE_URL}/countCheques`);
            if (!response.ok) {
                throw new Error('Failed to fetch pending cheque count');
            }
            const result = await response.json();
            setPendingChequeCount(result.count);
        } catch (error) {
            setError(error.message);
        }
    };

    useEffect(() => {
        const fetchChequeData = async () => {
            try {
                const pendingResponse = await fetch(`${config.BASE_URL}/pendingChequeTotal`);
                const clearedResponse = await fetch(`${config.BASE_URL}/clearedChequeTotal`);

                if (!pendingResponse.ok || !clearedResponse.ok) {
                    throw new Error('Failed to fetch cheque amounts');
                }

                const pendingResult = await pendingResponse.json();
                const clearedResult = await clearedResponse.json();

                setPendingChequeAmount(pendingResult.totalAmount || 0);
                setClearedChequeAmount(clearedResult.totalAmount || 0);
            } catch (error) {
                console.error('Error fetching cheque data:', error);
            }
        };

        fetchChequeData();
    }, []);

    return (
        <div>
            <div className="scrolling-container">
                <div className="mt-4">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: "20px" }}>
                        <div style={{ paddingLeft: "10px", width: "100%" }}>

                            <h4 style={{ marginBottom: "24px", fontSize: "24px", color: "#2d3436", fontWeight: "600" }}>
                                Cheques Drawn
                            </h4>

                            <div style={{ display: "flex", gap: "20px", alignItems: "flex-start", flexWrap: "nowrap", overflowX: "auto", paddingBottom: "10px" }}>

                                <div style={{ flex: "0 0 240px", backgroundColor: "#ffffff", padding: "16px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)" }}>
                                    <div style={{ fontSize: "14px", color: "#636e72", marginBottom: "8px" }}>Filter</div>
                                    <select style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid #dfe6e9", fontSize: "14px", backgroundColor: "#f8f9fa", outline: "none", cursor: "pointer" }}
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="all">All Payments</option>
                                        <option value="Pending">Pending Payments</option>
                                        <option value="Cleared">Cleared Payments</option>
                                    </select>
                                </div>

                                <div style={{ flex: "1", backgroundColor: "#ffffff", padding: "16px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", minWidth: "220px" }}>
                                    <div style={{ fontSize: "14px", color: "#636e72", marginBottom: "12px" }}>Payments To Be Honoured</div>
                                    <input
                                        type="text"
                                        style={{ width: "100%", padding: "12px", fontSize: "18px", fontWeight: "600", color: "#2ecc71", border: "none", backgroundColor: "transparent", textAlign: "right" }}
                                        value={pendingChequeCount}
                                        disabled
                                    />
                                </div>

                                <div style={{ flex: "1", backgroundColor: "#ffffff", padding: "16px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", minWidth: "220px" }}>
                                    <div style={{ fontSize: "14px", color: "#636e72", marginBottom: "12px" }}>Pending Amount</div>
                                    <input
                                        type="text"
                                        style={{ width: "100%", padding: "12px", fontSize: "18px", fontWeight: "600", color: "#2ecc71", border: "none", backgroundColor: "transparent", textAlign: "right" }}
                                        value={`LKR ${pendingChequeAmount.toLocaleString()}`}
                                        disabled
                                    />
                                </div>

                                <div style={{ flex: "1", backgroundColor: "#ffffff", padding: "16px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", minWidth: "220px" }}>
                                    <div style={{ fontSize: "14px", color: "#636e72", marginBottom: "12px" }}>Cleared Amount</div>
                                    <input
                                        type="text"
                                        style={{ width: "100%", padding: "12px", fontSize: "18px", fontWeight: "600", color: "#2ecc71", border: "none", backgroundColor: "transparent", textAlign: "right" }}
                                        value={`LKR ${clearedChequeAmount.toLocaleString()}`}
                                        disabled
                                    />
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

                <Table
                    search="Search by Supplier Name"
                    data={filteredData}
                    columns={columns}
                    showButton={false}
                    showDate={true}
                    showActions={false}
                    showDelete={false}
                />
            </div>
        </div>
    );
}

export default SupplierChequePayments;